import {
    ResponsiveContainer,
    LineChart,
    Line,
    ReferenceArea,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
} from 'recharts'
import { CategoricalChartState } from 'recharts/types/chart/types'

import {
    getGraphBackgroundColor,
    getGraphOverlayColor,
    getGraphPrimaryColor,
    getGraphStrokeColor,
    GRAPH_BORDER_STYLE,
    GRAPH_DEFAULT_DOT_RADIUS,
    GRAPH_DEFAULT_DOT_STROKE,
    GRAPH_LINE_STYLE,
    GRAPH_STROKED_DOT_RADIUS,
} from './graph-constants'
import Vector2 from '../../models/Vector2'
import { range, rangeInclusive } from '../../tools/range'
import t from '../../tools/t'
import { PropsWithChildren } from 'react'

const OOB_AREA_RATIO = 0.2
const GRAPH_HEIGHT = 240
const GRAPH_RESOLUTION = 5.63333
const GRAPH_WIDTH = Math.round(GRAPH_HEIGHT * GRAPH_RESOLUTION)

export interface LineGraphProps extends PropsWithChildren {
    min: number
    max: number
    avg?: number
    points: Vector2[]
    onPointClick?: (point: Vector2, index: number) => void
    drawOobArea?: boolean
}

export default function LineGraph({
    min,
    max,
    avg,
    points,
    onPointClick,
    drawOobArea,
    children,
}: LineGraphProps) {
    if (!(typeof min === 'number' && typeof max === 'number')) return

    // Ensure some level of out-of-bounds exist.
    // If 20% of the range doesn't produce any area, fix oob to 1.
    const oobSize = Math.floor((max - min) * OOB_AREA_RATIO) || 1
    const yRange = rangeInclusive(Math.ceil(min - oobSize), Math.floor(max + oobSize))
    const xRange = range(0, points.length)

    const backgroundColor = getGraphBackgroundColor()
    const primaryColor = getGraphPrimaryColor()
    const overlayColor = getGraphOverlayColor()
    const strokeColor = getGraphStrokeColor()

    const onLineClick = (state: CategoricalChartState) => {
        if (!onPointClick) return
        const index = state.activeTooltipIndex
        if (typeof index !== 'number') return
        const point = points[index]
        if (!point) return
        onPointClick(point, index)
    }

    // In some edge-cases points may fall outside the visible scope, and we instead use the min and max value to calculate the graph's actual minimum.
    // This is not intended behavior, but if not handled points outside the domain will break the graph when shown.
    const absoluteMin = points.reduce((min, p) => (p.y < min ? p.y : min), Infinity)
    const absoluteMax = points.reduce((max, p) => (p.y > max ? p.y : max), -Infinity)

    return (
        <div style={{ height: GRAPH_HEIGHT, width: '100%' }}>
            {/* Dimensions are controlled outside the responsive container, which will just fill its parent. */}
            {/* Without the responsive container, relative distances inside the graph will simply be wrong. */}
            <ResponsiveContainer width="100%" height="100%">
                {/* NOTE: dimensions here are used to generate the vector graphic. */}
                <LineChart
                    className="graph"
                    height={GRAPH_HEIGHT}
                    width={GRAPH_WIDTH}
                    data={points}
                    onClick={onLineClick}
                >
                    <XAxis
                        tick={false}
                        allowDataOverflow
                        domain={[
                            (dataMin: number) => dataMin - 1,
                            (dataMax: number) => dataMax + 1,
                        ]}
                        type="number"
                        dataKey="x"
                        ticks={xRange}
                        tickLine={GRAPH_LINE_STYLE}
                        axisLine={GRAPH_LINE_STYLE}
                    />

                    <YAxis
                        allowDecimals={false}
                        type="number"
                        dataKey="y"
                        tickSize={0}
                        tickMargin={12}
                        tickFormatter={(value) => Math.round(value).toString()}
                        tickCount={6}
                        tickLine={GRAPH_LINE_STYLE}
                        axisLine={GRAPH_LINE_STYLE}
                        domain={[Math.ceil(min - oobSize), Math.floor(max + oobSize)]}
                    />

                    <CartesianGrid {...GRAPH_BORDER_STYLE} vertical={false} />

                    {/* Minimum boundary */}
                    {drawOobArea && (
                        <>
                            <ReferenceArea
                                x1={-1}
                                x2={points.length}
                                y1={Math.min(min - oobSize, absoluteMin)}
                                y2={min}
                                fill={overlayColor}
                                ifOverflow="visible"
                                pointerEvents="none"
                            />
                            <ReferenceLine
                                y={min}
                                stroke={primaryColor}
                                label={{
                                    value: t('common_min').toUpperCase(),
                                    fill: primaryColor,
                                    fontSize: 12,
                                    position: 'left',
                                    offset: -25,
                                    dy: 8,
                                }}
                            />
                        </>
                    )}

                    {/* Maximum boundary */}
                    {drawOobArea && (
                        <>
                            <ReferenceArea
                                x1={-1}
                                x2={points.length}
                                y1={Math.max(max + oobSize, absoluteMax)}
                                y2={max}
                                fill={overlayColor}
                                ifOverflow="visible"
                                pointerEvents="none"
                            />
                            <ReferenceLine
                                y={max}
                                stroke={primaryColor}
                                label={{
                                    value: t('common_max').toUpperCase(),
                                    fill: primaryColor,
                                    fontSize: 12,
                                    position: 'left',
                                    offset: -28,
                                    dy: -8,
                                }}
                            />
                        </>
                    )}

                    {/* Average */}
                    {points.length > 1 && typeof avg === 'number' && (
                        <ReferenceLine
                            y={avg}
                            stroke={strokeColor}
                            strokeDasharray={GRAPH_BORDER_STYLE.strokeDasharray}
                            label={{
                                value: t('common_avg').toUpperCase(),
                                fill: strokeColor,
                                fontSize: 12,
                                position: 'left',
                                offset: -25,
                                dy: -8,
                            }}
                        />
                    )}

                    <Line
                        dataKey="y"
                        type="monotone"
                        stroke="transparent"
                        fill={primaryColor}
                        dot={{ r: GRAPH_DEFAULT_DOT_RADIUS, fill: primaryColor }}
                        activeDot={{
                            r: GRAPH_STROKED_DOT_RADIUS,
                            strokeWidth: GRAPH_DEFAULT_DOT_STROKE,
                            fill: backgroundColor,
                            stroke: primaryColor,
                            cursor: 'pointer',
                        }}
                    />

                    {/* Include any extra recharts component through children */}
                    {children}
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}
